import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { createInvitation } from "../api/invitations";
import { useHostToast } from "../../host/useHostToast";
import { useHostRecaptcha } from "../../host/useHostRecaptcha";
import { useTrack } from "../../host/useTrack";
import { useCurrentUser } from "../../host/useCurrentUser";
import { ApiError } from "../../api";
import type { CreateInvitationInput } from "../types";

const RECAPTCHA_ACTION = "invite_member";
const MY_TEAM = "My Team";

interface UseInviteMembers {
  pending: boolean;
  error: string | null;
  invite: (input: CreateInvitationInput) => Promise<boolean>;
  clearError: () => void;
}

// Pulls the user-facing message out of an upstream error body — the
// general-api proxy forwards it under `body.upstream` (see backend's
// general-api.errors.ts). Falls back to a generic copy if missing.
function extractMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === "object") {
    const body = err.body as { upstream?: { message?: string } };
    if (typeof body.upstream?.message === "string" && body.upstream.message) {
      return body.upstream.message;
    }
  }
  return fallback;
}

export function useInviteMembers(): UseInviteMembers {
  const { t } = useTranslation();
  const toast = useHostToast();
  const executeRecaptcha = useHostRecaptcha();
  const track = useTrack();
  const currentUser = useCurrentUser();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invite = useCallback(
    async (input: CreateInvitationInput) => {
      setPending(true);
      setError(null);
      const emailsLabel = input.emails.join(",");
      const baseProps = {
        eventCategory: MY_TEAM,
        eventLabel: emailsLabel,
        plan: currentUser?.planType,
        userId: currentUser?.id,
        roleInvited: input.accountRole,
      };
      // Mirrors InviteNewMemberV2.handleSendInvitation submit-attempt
      // tracking (the has-team branch — we don't support create-team).
      track("Team management > Invite member > Send invitation", baseProps);
      try {
        const token = await executeRecaptcha(RECAPTCHA_ACTION);
        await createInvitation(input, { token, bypass: !token });
        // Mirrors the success-path tracking.
        track("Team management > Invite member > Invited successfully", {
          ...baseProps,
          role: input.accountRole,
        });
        // Mirrors the reCAPTCHA-verified breadcrumb. Original passes a
        // string as the second arg here (likely a bug — the function
        // expects an object), so we shape it as a payload field.
        track("Invite Member", { eventLabel: "reCAPTCHA v3 verified" });
        toast.success(t("team.toast.invitationsSent"));
        return true;
      } catch (e: unknown) {
        // Mirrors the failure-path tracking. Rate-limit (429) gets its
        // own event in the original — preserved here.
        const status = e instanceof ApiError ? e.status : undefined;
        if (status === 429) {
          track("Team management > Invite member > Limit exceeded", {
            eventCategory: MY_TEAM,
          });
        } else {
          const msg = extractMessage(e, t("team.invite.submitError"));
          track("Team management > Invite member > Invite failed", {
            eventCategory: MY_TEAM,
            eventLabel: msg,
          });
        }
        // Inline-only: the modal renders this below the form, matching
        // the original presenter UX. No toast — would be redundant.
        setError(extractMessage(e, t("team.invite.submitError")));
        return false;
      } finally {
        setPending(false);
      }
    },
    [executeRecaptcha, t, toast, track, currentUser?.planType, currentUser?.id],
  );

  const clearError = useCallback(() => setError(null), []);

  return { invite, pending, error, clearError };
}
